import { useCallback, useSyncExternalStore } from 'react'

import type { ExtendedLocalStorageKey, ExtendedLocalStorageValueMap } from '@core/app/types'
import { setLocalStorageItem, removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'

const snapshotCache = new Map<string, () => any>()

function isLocalStorageAvailable(): boolean {
    if (typeof localStorage === 'undefined' || localStorage === null) {
        return false
    }

    try {
        const test = '__storage_test__'
        localStorage.setItem(test, test)
        localStorage.removeItem(test)
        return true
    } catch (e) {
        return false
    }
}

class MemoryStorage {
    private store = new Map<string, string>()

    getItem(key: string): string | null {
        return this.store.get(key) || null
    }

    setItem(key: string, value: string): void {
        this.store.set(key, value)
    }

    removeItem(key: string): void {
        this.store.delete(key)
    }

    clear(): void {
        this.store.clear()
    }
}

const storage = isLocalStorageAvailable() ? localStorage : new MemoryStorage()

function subscribe(callback: () => void): () => void {
    const handler = () => callback()
    window.addEventListener('localStorageChange', handler)
    window.addEventListener('storage', handler)
    return () => {
        window.removeEventListener('localStorageChange', handler)
        window.removeEventListener('storage', handler)
    }
}

function getOrCreateSnapshot<K extends ExtendedLocalStorageKey>(
    key: K,
    defaultValue?: ExtendedLocalStorageValueMap[K]
): () => ExtendedLocalStorageValueMap[K] | null {
    if (snapshotCache.has(key)) {
        return snapshotCache.get(key) as () => ExtendedLocalStorageValueMap[K] | null
    }

    let lastRaw: string | null = null
    let lastParsed: ExtendedLocalStorageValueMap[K] | null = null

    const snapshotFn = () => {
        let raw: string | null = null
        try {
            raw = storage.getItem(key)
        } catch (error) {
            console.error(`Failed to read from storage for key "${key}":`, error)
            return defaultValue ?? null
        }

        if (raw === lastRaw) return lastParsed

        lastRaw = raw
        try {
            lastParsed = raw ? (JSON.parse(raw) as ExtendedLocalStorageValueMap[K]) : (defaultValue ?? null)
        } catch (parseError) {
            console.error(`Failed to parse storage data for key "${key}":`, parseError)
            lastParsed = defaultValue ?? null
        }
        return lastParsed
        // const raw = localStorage.getItem(key) // TODO - old test
        // if (raw === lastRaw) return lastParsed
        //
        // lastRaw = raw
        // try {
        //     lastParsed = raw ? (JSON.parse(raw) as ExtendedLocalStorageValueMap[K]) : (defaultValue ?? null)
        // } catch {
        //     lastParsed = defaultValue ?? null
        // }
        // return lastParsed
    }

    snapshotCache.set(key, snapshotFn)
    return snapshotFn
}

function useLocalStorageStore<K extends ExtendedLocalStorageKey>(
    key: K,
    defaultValue?: ExtendedLocalStorageValueMap[K]
): [ExtendedLocalStorageValueMap[K] | null, (value: ExtendedLocalStorageValueMap[K]) => void, () => void] {
    const value = useSyncExternalStore(subscribe, getOrCreateSnapshot(key, defaultValue))

    const setValue = useCallback(
        (val: ExtendedLocalStorageValueMap[K]) => {
            setLocalStorageItem(key, val)
            window.dispatchEvent(new CustomEvent('localStorageChange'))
        },
        [key]
    )

    const removeValue = useCallback(() => {
        removeLocalStorageItem(key)
        window.dispatchEvent(new CustomEvent('localStorageChange'))
    }, [key])

    return [value, setValue, removeValue]
}

export default useLocalStorageStore
